<script setup lang="ts">
import { CheckSquare, Copy, Maximize2, Pencil, RotateCw, Trash2, XSquare } from 'lucide-vue-next'
import FlatSelect from '../ui/FlatSelect.vue'
import TileCropperCanvas from './TileCropperCanvas.vue'
import GuidesOverlay from './GuidesOverlay.vue'
import Frame2dOverlay from './Frame2dOverlay.vue'
import { frame2dPadFraction, type Frame2d } from '../../preview3d/flat2d'
import { computed } from 'vue'

const props = defineProps<{
  img: any
  vIdx: number
  selected: boolean
  skeleton: boolean
  layoutVersion: number
  displayUrl: string
  fitInMode: boolean
  whiteBorderLabel?: string | null
  /** Multi: Optionen nur als Vorschau (noch nicht übernommen) */
  optionPreview?: boolean
  mountTileCropper: boolean
  showUploadingOverlay: boolean
  uploadIssueLabel?: string
  uploadIssueTone?: 'processing' | 'failed' | null
  preview3d: boolean
  /** Rahmen (Schattenfuge, Bilderrahmen, Posterleiste) für die 2D-Ansicht, null = ohne */
  frame2d?: Frame2d | null
  tileQualityTone: string
  tileQualityLabel: string
  tileQualityText: string
  tileProductValue: string | null
  productOptions: any[]
  sizeOptions: any[]
  visibleGroups: any[]
  sizeKey: string | null
  observerRef?: ((el: any, id: string) => void) | null
  cropperRef?: ((el: any, id: string) => void) | null
  fitFrameStyle: (img: any) => any
  stencilProps: (img: any) => any
  inactiveCropWindowStyle: (img: any) => any
  inactiveCropImageStyle: (img: any) => any
  tileGroupValue: (img: any, group: any) => any
  tileGroupOptions: (group: any) => any[]
}>()

const emit = defineEmits<{
  (e: 'tile-click', ev: MouseEvent, id: string, vIdx: number): void
  (e: 'tile-dblclick', ev: MouseEvent, id: string): void
  (e: 'tile-enter', id: string): void
  (e: 'select-click', ev: MouseEvent, id: string, vIdx: number): void
  (e: 'remove', id: string): void
  (e: 'tag-click', tag: string): void
  (e: 'quality-open', ev: MouseEvent | FocusEvent, text: string): void
  (e: 'quality-move', ev: MouseEvent): void
  (e: 'quality-close'): void
  (e: 'product-change', img: any, value: string): void
  (e: 'size-change', img: any, value: string): void
  (e: 'group-change', img: any, group: any, value: string): void
  (e: 'qty-step', img: any, step: number): void
  (e: 'qty-input', img: any, value: number): void
  (e: 'qty-persist', img: any): void
  (e: 'duplicate', id: string): void
  (e: 'details', id: string): void
  (e: 'fullscreen', img: any): void
  (e: 'preview3d', img: any): void
  (e: 'rotate', img: any, deg: number): void
  (e: 'retry-upload', id: string): void
  (e: 'cropper-ready', img: any, payload: any): void
  (e: 'cropper-complete', img: any, payload: any): void
}>()

/** Rand in % der Kachel, damit der Rahmen um den Ausschnitt Platz hat (gleich wie im Zuschnitt-Canvas) */
const framePadPct = computed(() => frame2dPadFraction(props.frame2d ?? null) * 100)
const frameInset = computed(() => (props.frame2d ? { inset: `${framePadPct.value}%` } : undefined))
</script>

<template>
  <article
    :ref="(el:any) => observerRef?.(el, img.id)"
    :data-image-id="img.id"
    class="tile"
    :class="{ selected, locked: img.ui.locked, skeleton }"
    tabindex="0"
    @click="emit('tile-click', $event, img.id, vIdx)"
    @dblclick.stop="emit('tile-dblclick', $event, img.id)"
    @keyup.enter="emit('tile-enter', img.id)"
  >
    <div class="tile-topbar" @click.stop>
      <button
        class="delete-chip"
        type="button"
        @click.stop="emit('remove', img.id)"
        title="Bild löschen"
        aria-label="Bild löschen"
      >
        <Trash2 :size="14" />
      </button>

      <div class="tile-title" :title="img.name">
        {{ img.name }}
        <span v-if="Array.isArray(img.record?.tags) && img.record.tags.length" class="tile-tags">
          <button v-for="tag in img.record.tags" :key="tag" type="button" class="tile-tag" :title="`Nur „${tag}“ anzeigen`" @click.stop="emit('tag-click', tag)">{{ tag }}</button>
        </span>
      </div>

      <div
        class="tile-quality"
        :data-tone="tileQualityTone"
        :aria-label="tileQualityLabel"
        tabindex="0"
        @mouseenter="emit('quality-open', $event, tileQualityText)"
        @mousemove="emit('quality-move', $event)"
        @mouseleave="emit('quality-close')"
        @focus="emit('quality-open', $event, tileQualityText)"
        @blur="emit('quality-close')"
      >
        <i class="tile-quality__dot" aria-hidden="true" />
      </div>

      <button
        class="check"
        type="button"
        :aria-pressed="selected"
        @click.stop="emit('select-click', $event, img.id, vIdx)"
        title="Auswählen"
        aria-label="Auswählen"
      >
        <span class="box" :class="{ on: selected }"><span class="tick">✓</span></span>
      </button>
    </div>

    <div class="preview" :class="{ 'preview--uploading': showUploadingOverlay }" @dragstart.prevent @click.stop="emit('tile-click', $event, img.id, vIdx)">
      <template v-if="displayUrl">
        <div v-if="fitInMode" class="fit-preview-shell" :class="{ 'fit-preview-shell--pending': img.ui.previewLoading }" :style="frame2d ? { padding: `calc(12px + ${framePadPct}%)` } : undefined">
          <div class="fit-preview-frame" :class="{ 'fit-preview-frame--white-border': !!whiteBorderLabel }" :style="fitFrameStyle(img)">
            <img
              :src="displayUrl"
              :alt="img.name"
              class="fit-preview-image"
              :class="{ 'fit-preview-image--pending': img.ui.previewLoading, 'fit-preview-image--grayscale': !!img.record?.edits?.preset?.grayscale }"
              draggable="false"
            />
            <span v-if="whiteBorderLabel" class="white-border-preview" aria-hidden="true" />
            <Frame2dOverlay v-if="frame2d" :frame="frame2d" class="frame2d-fill" />
          </div>
        </div>
        <div v-else-if="mountTileCropper" class="tile-preview-active-shell" :class="{ 'tile-preview-active-shell--pending': img.ui.previewLoading }">
          <TileCropperCanvas
            :key="'canvas:' + img.id + ':' + displayUrl + ':' + img.ui.rev"
            :ref="(el:any) => cropperRef?.(el, img.id)"
            :src="displayUrl"
            :grayscale="!!img.record?.edits?.preset?.grayscale"
            :rotation="img.crop?.rot || 0"
            class="cropper mi-cropper"
            :disabled="img.ui.locked"
            :coordinates="img.crop ? { left: img.crop.x, top: img.crop.y, width: img.crop.w, height: img.crop.h } : null"
            :image-width="img.ui.baseW || img.ui.imgW"
            :image-height="img.ui.baseH || img.ui.imgH"
            :stencil-props="stencilProps(img)"
            @tap="emit('tile-click', $event as any, img.id, vIdx)"
            @ready="emit('cropper-ready', img, $event)"
            @complete="emit('cropper-complete', img, $event)"
          />
        </div>
        <div v-else class="inactive-crop-shell" :class="{ 'inactive-crop-shell--pending': img.ui.previewLoading }">
          <div class="tile-preview-lite-shell" :style="frameInset">
            <img
              :src="displayUrl"
              :alt="img.name"
              class="tile-preview-lite-base"
              :class="{ 'tile-preview-lite-base--pending': img.ui.previewLoading, 'tile-preview-lite-base--grayscale': !!img.record?.edits?.preset?.grayscale }"
              draggable="false"
            />
            <div
              class="tile-preview-lite-window"
              :class="{ 'tile-preview-lite-window--pending': img.ui.previewLoading }"
              :style="inactiveCropWindowStyle(img)"
            >
              <img
                :src="displayUrl"
                :alt="img.name"
                class="inactive-crop-image"
                :class="{ 'inactive-crop-image--pending': img.ui.previewLoading, 'inactive-crop-image--grayscale': !!img.record?.edits?.preset?.grayscale }"
                :style="inactiveCropImageStyle(img)"
                draggable="false"
              />
              <span v-if="whiteBorderLabel" class="white-border-preview" aria-hidden="true" />
              <GuidesOverlay
                v-if="stencilProps(img)?.geometry"
                :geometry="stencilProps(img)?.geometry"
                :aspect="img.crop && img.crop.h ? img.crop.w / img.crop.h : null"
                :mode="stencilProps(img)?.guides || 'full'"
                compact
              />
            </div>
          </div>
          <div v-if="frame2d" class="frame2d-stage" :style="frameInset">
            <Frame2dOverlay :frame="frame2d" :style="inactiveCropWindowStyle(img)" />
          </div>
        </div>
      </template>
      <div v-else class="tile-preview-empty" aria-hidden="true" />
      <span
        v-if="optionPreview"
        class="tile-preview-badge"
        title="Vorschau – erst mit „Auswahl übernehmen“ gespeichert"
      >Vorschau</span>
      <span
        v-if="whiteBorderLabel"
        class="tile-border-badge"
        :title="`Option „${whiteBorderLabel}“ gewählt: Das Bild wird mit weißem Rand gedruckt.`"
      >
        <i class="tile-border-badge__icon" aria-hidden="true" />
        Weißer Rand
      </span>
    </div>

    <div v-if="showUploadingOverlay" class="tile-uploading-overlay" aria-live="polite">
      <span class="tile-uploading-overlay__label">Wird hochgeladen</span>
    </div>
    <div v-else-if="uploadIssueLabel" class="tile-status-badge" :data-tone="uploadIssueTone || 'processing'" aria-live="polite">
      {{ uploadIssueLabel }}
    </div>
    <button
      v-if="uploadIssueTone === 'failed'"
      class="tile-status-action"
      type="button"
      @click.stop="emit('retry-upload', img.id)"
    >
      Erneut versuchen
    </button>

    <footer class="meta" @click.stop @dblclick.stop>
      <div class="erow sizeline">
        <div class="control-stack">
          <div class="control-row control-row--split">
            <div class="control-cell">
              <FlatSelect
                :model-value="tileProductValue"
                :options="productOptions"
                placeholder="Produkt wählen…"
                no-search
                :disabled="!productOptions.length"
                @update:model-value="emit('product-change', img, String($event || ''))"
              />
            </div>
            <div class="control-cell">
              <FlatSelect
                :model-value="sizeKey || null"
                :options="sizeOptions"
                placeholder="Größe wählen…"
                :show-selected-right="false"
                no-search
                :disabled="img.ui.locked || !sizeOptions.length"
                @update:model-value="emit('size-change', img, String($event || ''))"
              />
            </div>
          </div>

          <div v-for="g in visibleGroups" :key="`tg-${img.id}-${g.id}`" class="control-row">
            <div class="control-cell control-cell--full">
              <FlatSelect
                :model-value="tileGroupValue(img, g)"
                :options="tileGroupOptions(g)"
                :placeholder="`${g.title || 'Variante'} wählen`"
                no-search
                :disabled="img.ui.locked || !tileGroupOptions(g).length"
                @update:model-value="emit('group-change', img, g, String($event || ''))"
              />
            </div>
          </div>

          <div class="control-row control-row--qty" @dblclick.stop>
            <button class="qty-step" type="button" @click.stop="emit('qty-step', img, -1)" @dblclick.stop.prevent :disabled="img.ui.locked">−</button>
            <input
              :value="img.ui.quantity"
              class="qty-input"
              type="number"
              min="1"
              :disabled="img.ui.locked"
              @click.stop
              @dblclick.stop
              @input="emit('qty-input', img, Number(($event.target as HTMLInputElement).value))"
              @change="emit('qty-persist', img)"
            />
            <button class="qty-step" type="button" @click.stop="emit('qty-step', img, 1)" @dblclick.stop.prevent :disabled="img.ui.locked">+</button>
          </div>
        </div>
      </div>

      <div class="erow actions" :class="{ 'actions--five': preview3d, 'actions--four': !preview3d }">
        <button class="btn mini icon-only tile-action" @click.stop="emit('duplicate', img.id)" title="Duplizieren" aria-label="Duplizieren">
          <Copy :size="16" />
        </button>
        <button class="btn mini icon-only tile-action" @click.stop="emit('details', img.id)" title="Details bearbeiten" aria-label="Details bearbeiten">
          <Pencil :size="16" />
        </button>
        <button class="btn mini icon-only tile-action" @click.stop="emit('fullscreen', img)" title="Vollbild" aria-label="Vollbild">
          <Maximize2 :size="16" />
        </button>
        <button
          v-if="preview3d"
          class="btn mini icon-only tile-action"
          @click.stop="emit('preview3d', img)"
          title="3D-Vorschau"
          aria-label="3D-Vorschau öffnen"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        </button>
        <button
          class="btn mini icon-only tile-action"
          @click.stop="emit('rotate', img, 90)"
          :disabled="fitInMode"
          title="Ausschnitt drehen"
          aria-label="Ausschnitt drehen"
        >
          <RotateCw :size="16" />
        </button>
      </div>
    </footer>
  </article>
</template>

<style scoped>
.tile{
  position: relative;
  border: 1px solid var(--line);
  background: #fff;
  border-radius: 4px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
}
.tile.selected{
  outline: none;
  box-shadow: none;
}
.tile.selected::after{
  content: "";
  position: absolute;
  inset: 0;
  border: 2px solid rgba(239,68,68,.65);
  border-radius: 4px;
  pointer-events: none;
  z-index: 25;
}
.preview{
  position: relative;
  aspect-ratio: 1 / 1;
  height: auto;
  background: #e5e7eb;
  overflow: visible;
}
.preview--uploading{ opacity: .52; }
.tile-preview-empty{
  position: absolute;
  inset: 0;
  background: transparent;
  pointer-events: none;
}

.fit-preview-shell{
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: #d1d5db;
}
.fit-preview-shell--pending{
  background: linear-gradient(180deg, rgba(209,213,219,.78), rgba(209,213,219,.92));
}
.fit-preview-frame{
  position: relative;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(15,23,42,.08);
}
.fit-preview-image{
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.fit-preview-image--grayscale,
.tile-preview-lite-base--grayscale,
.inactive-crop-image--grayscale{
  filter: grayscale(1);
}
.fit-preview-image--pending,
.inactive-crop-image--pending{
  opacity: .58;
}

.tile-preview-lite-shell{
  position: absolute;
  inset: 0;
  background: #d1d5db;
  overflow: hidden;
}
.tile-preview-lite-base{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  opacity: .82;
  filter: saturate(.92);
}
.tile-preview-lite-base--pending{ opacity: .52; }
.frame2d-stage{ position: absolute; pointer-events: none; z-index: 1; }
.frame2d-fill{ inset: 0; }
.tile-preview-lite-window{
  position: absolute;
  overflow: hidden;
  border: 1.5px solid rgba(255,255,255,.96);
  box-shadow: 0 0 0 999px rgba(10,14,24,.38), 0 0 0 1px rgba(15,23,42,.16) inset;
  border-radius: 2px;
}
.tile-preview-lite-window--pending{
  border-color: rgba(255,255,255,.82);
  box-shadow: 0 0 0 999px rgba(10,14,24,.24), 0 0 0 1px rgba(15,23,42,.12) inset;
}
.tile-preview-active-shell{
  position: absolute;
  inset: 0;
  background: #d1d5db;
}
.tile-preview-active-shell--pending{
  background: linear-gradient(180deg, rgba(209,213,219,.72), rgba(209,213,219,.9));
}
.inactive-crop-shell{
  position: absolute;
  inset: 0;
  display: block;
  background: #d1d5db;
  overflow: hidden;
}
.inactive-crop-shell--pending{
  background: linear-gradient(180deg, rgba(209,213,219,.78), rgba(209,213,219,.92));
}
.inactive-crop-image{
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  user-select: none;
  pointer-events: none;
}
.tile-uploading-overlay{
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.tile-uploading-overlay__label{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(15,23,42,.66);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .01em;
  backdrop-filter: blur(4px);
}
/* Fit-In + weißer Rand: Bild sitzt innerhalb des Rands, sonst geht der Rand im
   ohnehin weißen Einpass-Hintergrund unter. */
.fit-preview-frame--white-border{
  padding: 3px;
  box-sizing: border-box;
}
/* Option „weißer Rand“ – nur Vorschau, nicht maßstabsgetreu */
.white-border-preview{
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow: inset 0 0 0 3px #fff, inset 0 0 0 4px rgba(15,23,42,.18);
}
.tile-preview-badge{
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .01em;
  color: #78350f;
  background: #fde68a;
  box-shadow: 0 2px 8px rgba(15,23,42,.18);
  white-space: nowrap;
  pointer-events: none;
}
.tile-border-badge{
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 20px;
  padding: 0 8px 0 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .01em;
  color: #fff;
  background: rgba(15,23,42,.72);
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(15,23,42,.18);
  white-space: nowrap;
}
.tile-border-badge__icon{
  width: 10px;
  height: 10px;
  border: 2px solid #fff;
  border-radius: 1px;
  box-sizing: border-box;
}
.tile-status-badge{
  position: absolute;
  right: 10px;
  top: 44px;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  max-width: 120px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .01em;
  color: #fff;
  background: rgba(217,119,6,.88);
  backdrop-filter: blur(4px);
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(15,23,42,.12);
}
.tile-status-badge[data-tone="failed"]{
  background: rgba(185,28,28,.92);
}
.tile-status-action{
  position: absolute;
  right: 10px;
  top: 70px;
  z-index: 6;
  min-height: 26px;
  padding: 0 10px;
  border: 0;
  border-radius: 999px;
  background: rgba(255,255,255,.96);
  color: #991b1b;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(15,23,42,.16);
}
.tile-status-action:hover{
  background: #fff;
}
.tile-preview-active-image-canvas{
  position: absolute;
  inset: 0;
  z-index: 1;
}
.tile-preview-active-shell--pending .tile-preview-active-image-canvas{ opacity: .52; }
.tile-preview-active-shell :deep(.cc),
.tile-preview-active-shell :deep(.cc-canvas){
  position: absolute;
  inset: 0;
}
.tile-preview-active-shell .cropper{ z-index: 2; }
.cropper{ width: 100%; height: 100%; }

.tile-topbar{
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0;
  background: #f3f4f6;
  border-bottom: 1px solid #d1d5db;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
.tile-title{
  flex: 1 1 auto;
  min-width: 0;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  color: rgba(15,23,42,.9);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-word;
  text-overflow: ellipsis;
  max-height: calc(2 * 1.2em);
  padding: 0 4px;
}
.check{
  flex-shrink: 0;
  width: 44px;
  height: 34px;
  border-left: 1px solid #d1d5db;
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  z-index: 20;
}
.delete-chip{
  flex-shrink: 0;
  width: 44px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-right: 1px solid #d1d5db;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
}
.delete-chip:hover{ background: #fff; color: #374151; }
.delete-chip:active{ transform: translateY(1px); }
.box{
  width: 16px;
  height: 16px;
  border: 1px solid rgba(15,23,42,.25);
  background: rgba(255,255,255,.92);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tick{ opacity: 0; font-size: 10px; transform: translateY(-1px); }
.box.on{ background: var(--accent); border-color: var(--accent); }
.box.on .tick{ opacity: 1; color: #fff; }

.meta{
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.erow{
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.sizeline{
  display: block;
  width: 100%;
  margin: 0;
}
.control-stack{
  width: 100%;
  border-top: 1px solid #d1d5db;
}
.control-row{
  display: flex;
  align-items: stretch;
  width: 100%;
  border-bottom: 1px solid #d1d5db;
}
.control-row--split .control-cell{ width: 50%; }
.control-row--split .control-cell + .control-cell{ border-left: 1px solid #d1d5db; }
.control-cell{
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
  min-width: 0;
}
.control-cell--full{ width: 100%; }
.control-row--qty{
  background: #f9fafb;
  height: 32px;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
}
.qty-step{
  width: 100%;
  height: 32px;
  border: 0;
  background: #fff;
  color: rgba(15,23,42,.78);
  font-size: 15px;
  font-weight: 500;
  line-height: 1;
}
.qty-step + .qty-input,
.qty-input + .qty-step{
  border-left: 1px solid #d1d5db;
}
.qty-input{
  width: 100%;
  min-width: 0;
  border: 0;
  height: 32px;
  background: #fff;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  color: rgba(15,23,42,.92);
  appearance: textfield;
  -moz-appearance: textfield;
}
.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button{
  -webkit-appearance: none;
  margin: 0;
}

.actions{
  display: grid;
  gap: 0;
  padding: 0;
  background: #f3f4f6;
  border-top: 1px solid #d1d5db;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
.actions--four{ grid-template-columns: repeat(4, minmax(0, 1fr)); }
.actions--five{ grid-template-columns: repeat(5, minmax(0, 1fr)); }
.tile-action{
  width: 100%;
  height: 40px;
  background: transparent;
  border: 0;
  padding: 0;
  color: #374151;
  box-shadow: none;
  border-radius: 0;
  min-width: 0;
}
.tile-action + .tile-action{ border-left: 1px solid #d1d5db; }
.tile-quality{
  width: 12px;
  height: 12px;
  margin: 0 6px 0 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  position: relative;
  cursor: help;
  flex: 0 0 auto;
}
.tile-quality:focus-visible{
  outline: 2px solid rgba(37,99,235,.35);
  outline-offset: 3px;
  border-radius: 999px;
}
.tile-quality__dot{
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(148,163,184,.32);
  box-shadow: inset 0 0 0 1px rgba(15,23,42,.08);
}
.tile-quality[data-tone='great'] .tile-quality__dot{ background: #16a34a; }
.tile-quality[data-tone='good'] .tile-quality__dot{ background: #65a30d; }
.tile-quality[data-tone='warn'] .tile-quality__dot{ background: #eab308; }
.tile-quality[data-tone='bad'] .tile-quality__dot{ background: #f59e0b; }

:deep(.control-cell .fs-trigger){
  height: 34px;
  padding: 0 10px;
  gap: 6px;
}
:deep(.control-cell .fs-label){ font-size: 12px; }
:deep(.control-cell .fs-right){ font-size: 11px; }
:deep(.control-cell .fs-caret){ font-size: 10px; }
:deep(.control-cell .fs-pop .fs-opt){
  height: 34px;
  padding: 0 8px;
  gap: 8px;
}
:deep(.control-cell .fs-pop .fs-opt .left){ font-size: 12px; }
:deep(.control-cell .fs-pop .fs-opt .right){ font-size: 11px; }
.tile-tags{ display:inline-flex; flex-wrap:wrap; gap:3px; margin-left:4px; vertical-align:middle; }
.tile-tag{ border:0; border-radius:999px; padding:0 6px; height:16px; line-height:16px; font-size:10px; background:rgba(227,11,23,.1); color:var(--pfe-color-accent,#e30b17); cursor:pointer; max-width:90px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.tile-tag:hover{ background:rgba(227,11,23,.2); }
</style>
